import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { StatistikNormTimebudgetModal } from '../statistik-norm-timebudget-modal/statistik-norm-timebudget-modal';
import { ExcelService } from '@store';
import { StatistikNorm } from '../../models/StatistikNorm';
import { StatistikService } from '../../statistik.service';
import { firstDayLastMonth } from '../../../shared/month-picker/month-picker.helper';
import { saveAs } from 'file-saver';

@Component({
    selector: 'app-statistik-normtid-ui',
    templateUrl: './statistik-normtid.component.html',
    styleUrls: ['./statistik-normtid.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class StatistikNormtidComponent implements OnInit {
    @Input() statistikNormTid: StatistikNorm[];
    @Input() periode: string;
    @Output() periodeChange = new EventEmitter<string>();
    expandedAfdelinger: string[] = [];
    selectedGruppeId: number;
    months: number[] = Array.from({ length: 12 }, (_, index) => index + 1);
    monthIndexArray = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
    monthsName: string[] = [
        'Januar', 'Februar', 'Marts', 'April', 'Maj', 'Juni',
        'Juli', 'August', 'September', 'Oktober', 'November', 'December'
    ];
    monthOrder: number[] = [];
    decimal: number;
    inDeciminuts: boolean;

    constructor(
        private modalService: NgbModal,
        private statistikService: StatistikService,
        private excel: ExcelService,
    ) {
        this.decimal = 2;
    }

    ngOnInit() {
        this.onPeriodeChange(firstDayLastMonth());
    }

    toggleAfdelinger() {
        if (this.expandedAfdelinger.length === 0) {
            this.expandedAfdelinger = this.getUniqueAfdelinger()?.map(afdeling => afdeling);
            return;
        }
        this.expandedAfdelinger = [];
    }

    toggleAfdeling(afdeling: string) {
        if (this.expandedAfdelinger.includes(afdeling)) {
            this.collapseAfdeling(afdeling);
            return;
        }
        this.expandAfdeling(afdeling);
    }

    expandAfdeling(afdeling: string) {
        this.expandedAfdelinger = [...this.expandedAfdelinger, afdeling];
    }

    collapseAfdeling(afdeling: string) {
        this.expandedAfdelinger = this.expandedAfdelinger
            .filter(showAfdeling => afdeling !== showAfdeling);
    }

    isMedarbejderVisible(medarbejder: StatistikNorm): boolean {
        const normtidTimerSum = medarbejder.normtid.reduce((sum, current) => {
            return sum + current.timer;
        }, 0);
        const plantidTimerSum = medarbejder.plantid.reduce((sum, current) => {
            return sum + current.timer;
        }, 0);

        if (normtidTimerSum === 0 && plantidTimerSum === 0) {
            return false;
        }
        if (!this.expandedAfdelinger.includes(medarbejder.afdeling) && this.getUniqueAfdelinger().length !== 1) {
            return false;
        }
        return true;
    }

    trackAfdelingNavn(_, afdeling: string) {
        return afdeling;
    }

    trackMedarbejderInit(_, medarbejder: StatistikNorm) {
        return medarbejder.medNavn;
    }

    async OpenTimebudgetModal(plantid: number, month: number, medarbejder: StatistikNorm) {
        if (plantid !== 0) {
            try {
                const year = this.periode.split('-')[0];
                const dateFormatted = `${year}-${month.toString().padStart(2, '0')}-01T00:00:00`;

                const subscription = this.statistikService.getPlantidModal(dateFormatted, medarbejder.medID).subscribe({
                    next: (data) => {
                        const plantidModal =  this.modalService.open(StatistikNormTimebudgetModal, {size: 'lg'});
                        plantidModal.componentInstance.plantid = data.sort((a, b) => a.kundeNr - b.kundeNr);
                        plantidModal.componentInstance.medid = medarbejder.medID;
                        plantidModal.componentInstance.month = month.toString().padStart(2, '0');
                        plantidModal.componentInstance.year = year;

                        plantidModal.result.finally(() => {
                            subscription.unsubscribe();
                        });
                    },
                    error: (error: any) => {
                        console.error('Error fetching data:', error);
                    }
                });
            } catch (error) {
                console.error('An error occurred:', error);
            }
        }
    }

    onPeriodeChange(periode: string) {
        const startDate = parseInt(periode.split('-')[1], 10);
        const numberOfMonths = 12;
        this.monthOrder = [];

        for (let i = 0; i < numberOfMonths; i++) {
            const monthNumber = (startDate + i - 1) % 12;
            this.monthOrder.push(monthNumber);
        }

        this.statistikNormTid = [];
        this.periodeChange.emit(periode);
    }

    getTotalPlantidPerMonthPerMedarbejder(medarbejder: StatistikNorm, month: number): number {
        return medarbejder.plantid.reduce((acc, plantid) => {
            return plantid.mth === month ? acc + plantid.timer : acc;
        }, 0);
    }

    getTotalNormtidPerMonthPerMedarbejder(medarbejder: StatistikNorm, month: number): number {
        return medarbejder.normtid.reduce((acc, normtid) => {
            const dateObject = new Date(normtid.dato);
            if (dateObject instanceof Date && dateObject.getMonth() + 1 === month) {
                return acc + normtid.timer;
            }
            return acc;
        }, 0);
    }

    getTotalNormtidPerMonth(medarbejdere: StatistikNorm[], month: number): number {
        let totalt = 0;

        medarbejdere
            .filter(m => !this.selectedGruppeId || m.group?.split(',').includes(this.selectedGruppeId?.toString()))
            .forEach(medarbejder => {
                totalt += this.getTotalNormtidPerMonthPerMedarbejder(medarbejder, month);
            }
        );
        return totalt;
    }

    getTotalPlantidPerMonth(medarbejdere: StatistikNorm[], month: number): number {
        let totalt = 0;

        medarbejdere
            .filter(m => !this.selectedGruppeId || m.group?.split(',').includes(this.selectedGruppeId?.toString()))
            .forEach(medarbejder => {
                totalt += this.getTotalPlantidPerMonthPerMedarbejder(medarbejder, month);
            }
        );
        return totalt;
    }

    getTotalNormtidPerYearPerMedarbejder(medarbejder: StatistikNorm): number {
        return medarbejder.normtid.reduce((acc, normtid) => {
                return acc + normtid.timer;
        }, 0);
    }

    getTotalPlantidPerYearPerMedarbejder(medarbejder: StatistikNorm): number {
        return medarbejder.plantid.reduce((acc, plantid) => {
            return acc + plantid.timer;
        }, 0);
    }

    getTotalNormtidPerYear(medarbejdere: StatistikNorm[]): number {
        let totalt = 0;
        medarbejdere
            .filter(m => !this.selectedGruppeId || m.group?.split(',').includes(this.selectedGruppeId?.toString()))
            .forEach(medarbejder => totalt += this.getTotalNormtidPerYearPerMedarbejder(medarbejder));
        return totalt;
    }

    getTotalPlantidPerYear(medarbejdere: StatistikNorm[]): number {
        let totalt = 0;
        medarbejdere
            .filter(m => !this.selectedGruppeId || m.group?.split(',').includes(this.selectedGruppeId?.toString()))
            .forEach(medarbejder => totalt += this.getTotalPlantidPerYearPerMedarbejder(medarbejder));
        return totalt;
    }

    getUniqueAfdelinger(): string[] {
        const uniqueAfdelinger = new Set<string>();
        this.statistikNormTid
            .filter(medarbejder => medarbejder.normtid.length > 0 || medarbejder.plantid.length > 0)
            .forEach(x => uniqueAfdelinger.add(x.afdeling));
        return Array.from(uniqueAfdelinger);
    }

    getMedarbejderFromAfdeling(afdeling: string): StatistikNorm[] {
        return this.statistikNormTid.filter(medarbejder => medarbejder.afdeling === afdeling);
    }

    getMonth(index: number) {
        return this.monthsName[index];
    }

    onSelectGruppe(id: number) {
        this.selectedGruppeId = id;
    }

    isMedarbejderInSelectedGruppe(medarbejder: StatistikNorm) {
        if (!this.selectedGruppeId) { return true; }
        return medarbejder.group
            ?.split(',')
            .map(gruppe => +gruppe)
            .includes(this.selectedGruppeId);
    }

    onDecimalSelected($event: number) {
        this.decimal = $event;
    }

    onDeciminutsSelected($event: boolean) {
        this.inDeciminuts = $event;
    }

    onExport() {
        const exportArray = [
            ['Afdeling', 'Medarbejder', ...this.monthIndexArray.map(monthIndex => [this.getMonth(this.monthOrder[monthIndex]), '']).flat(), 'I alt', ''],
            ['', '', ...Array(13).fill(['N', 'P']).flat()],
            ...this.statistikNormTid.map(medarbejder => [
                medarbejder.afdeling,
                medarbejder.medNavn,
                ...this.monthIndexArray.map(monthIndex => [
                    this.getTotalNormtidPerMonthPerMedarbejder(medarbejder, this.monthOrder[monthIndex] + 1),
                    this.getTotalPlantidPerMonthPerMedarbejder(medarbejder, this.monthOrder[monthIndex] + 1)]).flat(),
                this.getTotalNormtidPerYearPerMedarbejder(medarbejder),
                this.getTotalPlantidPerYearPerMedarbejder(medarbejder),
            ])
        ];
        this.excel.generateExcel(exportArray).subscribe((blob: Blob) => {
            saveAs(blob, 'statistik-norm-plan-tid.xlsx');
        });
    }
}
